import { Component } from "react";

interface Props {
	error: Error;
	componentStack: string | null;
	eventId: string | null;
	resetError: () => void;
}

interface State {
	consoleLogs: Array<{ type: string; message: string; timestamp: number }>;
	showLogs: boolean;
}

/**
 * Fallback UI for Sentry ErrorBoundary
 * Displays user-friendly error message with debug tools (on dev/staging)
 */
export class ErrorBoundaryFallback extends Component<Props, State> {
	private originalConsole: {
		log: typeof console.log;
		error: typeof console.error;
		warn: typeof console.warn;
	};
	private logBuffer: Array<{
		type: string;
		message: string;
		timestamp: number;
	}> = [];
	private isIntercepting = false;
	private shouldCaptureConsole =
		import.meta.env.VITE_BUILD_ENV === "staging" ||
		import.meta.env.VITE_BUILD_ENV === "development" ||
		import.meta.env.DEV;

	constructor(props: Props) {
		super(props);
		this.state = {
			consoleLogs: [],
			showLogs: false,
		};

		// Store original console methods
		this.originalConsole = {
			log: console.log.bind(console),
			error: console.error.bind(console),
			warn: console.warn.bind(console),
		};
	}

	override componentDidMount() {
		// Intercept console methods only in staging/development
		if (this.shouldCaptureConsole) {
			this.interceptConsole();
		}
	}

	override componentWillUnmount() {
		// Restore original console methods
		if (this.isIntercepting) {
			console.log = this.originalConsole.log;
			console.error = this.originalConsole.error;
			console.warn = this.originalConsole.warn;
		}
	}

	private interceptConsole() {
		this.isIntercepting = true;

		const addLog = (type: string, args: any[]) => {
			const message = args
				.map((arg) => {
					if (typeof arg === "object") {
						try {
							return JSON.stringify(arg, null, 2);
						} catch {
							return String(arg);
						}
					}
					return String(arg);
				})
				.join(" ");

			const logEntry = {
				type,
				message,
				timestamp: Date.now(),
			};

			// Use buffer to safely update state
			this.logBuffer.push(logEntry);

			// Batch update state to avoid frequent re-renders
			if (this.logBuffer.length === 1) {
				setTimeout(() => {
					this.setState((prevState) => ({
						consoleLogs: [...prevState.consoleLogs, ...this.logBuffer].slice(
							-100
						), // Keep last 100 logs
					}));
					this.logBuffer = [];
				}, 100);
			}
		};

		console.log = (...args: any[]) => {
			this.originalConsole.log(...args);
			addLog("log", args);
		};

		console.error = (...args: any[]) => {
			this.originalConsole.error(...args);
			addLog("error", args);
		};

		console.warn = (...args: any[]) => {
			this.originalConsole.warn(...args);
			addLog("warn", args);
		};
	}

	private copyLogsToClipboard = async () => {
		const logsText = this.state.consoleLogs
			.map((log) => {
				const time = new Date(log.timestamp).toLocaleTimeString();
				return `[${time}] ${log.type.toUpperCase()}: ${log.message}`;
			})
			.join("\n");

		const errorText = this.props.error
			? `\n\nERROR: ${this.props.error.message}\nSTACK: ${this.props.error.stack}\n\nSENTRY EVENT ID: ${this.props.eventId || "N/A"}`
			: "";

		const fullText = logsText + errorText;

		try {
			await navigator.clipboard.writeText(fullText);
			alert("Logi skopiowane do schowka!");
		} catch (err) {
			// Fallback for iOS
			const textArea = document.createElement("textarea");
			textArea.value = fullText;
			textArea.style.position = "fixed";
			textArea.style.left = "-999999px";
			document.body.appendChild(textArea);
			textArea.select();
			try {
				document.execCommand("copy");
				alert("Logi skopiowane do schowka!");
			} catch (e) {
				alert("Nie udało się skopiować logów");
			}
			document.body.removeChild(textArea);
		}
	};

	private handleReload = () => {
		this.props.resetError();
		window.location.reload();
	};

	override render() {
		return (
			<div
				style={{
					padding: "20px",
					maxHeight: "100vh",
					overflow: "auto",
					fontFamily:
						'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
					background: "#f5f5f5",
				}}
			>
				<div
					style={{
						background: "#fff",
						padding: "20px",
						borderRadius: "8px",
						marginBottom: "20px",
						boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
					}}
				>
					<h2 style={{ color: "#d32f2f", margin: "0 0 16px 0" }}>
						⚠️ Wystąpił błąd
					</h2>
					<p style={{ margin: "0 0 16px 0", color: "#666" }}>
						Aplikacja napotkała nieoczekiwany problem.
					</p>

					{this.props.eventId && (
						<p
							style={{
								margin: "0 0 16px 0",
								color: "#666",
								fontSize: "12px",
								fontFamily: "monospace",
							}}
						>
							🔍 Sentry Event ID: <strong>{this.props.eventId}</strong>
						</p>
					)}

					<div style={{ marginBottom: "16px" }}>
						<button
							onClick={this.handleReload}
							style={{
								padding: "12px 24px",
								background: "#1976d2",
								color: "white",
								border: "none",
								borderRadius: "4px",
								fontSize: "16px",
								fontWeight: "500",
								cursor: "pointer",
								marginRight: "8px",
							}}
						>
							🔄 Odśwież aplikację
						</button>
						{/* Pokaż przycisk logów tylko w staging/development */}
						{this.shouldCaptureConsole && (
							<>
								<button
									onClick={() =>
										this.setState((prev) => ({ showLogs: !prev.showLogs }))
									}
									style={{
										padding: "12px 24px",
										background: "#757575",
										color: "white",
										border: "none",
										borderRadius: "4px",
										fontSize: "16px",
										fontWeight: "500",
										cursor: "pointer",
										marginRight: "8px",
									}}
								>
									{this.state.showLogs ? "📋 Ukryj logi" : "📋 Pokaż logi"}
								</button>
								{this.state.showLogs && (
									<button
										onClick={this.copyLogsToClipboard}
										style={{
											padding: "12px 24px",
											background: "#4caf50",
											color: "white",
											border: "none",
											borderRadius: "4px",
											fontSize: "16px",
											fontWeight: "500",
											cursor: "pointer",
										}}
									>
										📋 Kopiuj logi
									</button>
								)}
							</>
						)}
					</div>

					{this.props.error && (
						<div
							style={{
								background: "#ffebee",
								padding: "12px",
								borderRadius: "4px",
								marginTop: "16px",
							}}
						>
							<div
								style={{
									fontWeight: "600",
									color: "#c62828",
									marginBottom: "8px",
								}}
							>
								Szczegóły błędu:
							</div>
							<div
								style={{
									fontFamily: "monospace",
									fontSize: "12px",
									color: "#d32f2f",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
								}}
							>
								{this.props.error.message}
							</div>
							{this.props.error.stack && (
								<details style={{ marginTop: "8px" }}>
									<summary style={{ cursor: "pointer", color: "#c62828" }}>
										Stack trace
									</summary>
									<pre
										style={{
											fontSize: "10px",
											overflow: "auto",
											margin: "8px 0 0 0",
											color: "#666",
										}}
									>
										{this.props.error.stack}
									</pre>
								</details>
							)}
							{this.props.componentStack && (
								<details style={{ marginTop: "8px" }}>
									<summary style={{ cursor: "pointer", color: "#c62828" }}>
										Component stack
									</summary>
									<pre
										style={{
											fontSize: "10px",
											overflow: "auto",
											margin: "8px 0 0 0",
											color: "#666",
										}}
									>
										{this.props.componentStack}
									</pre>
								</details>
							)}
						</div>
					)}
				</div>

				{/* Pokaż sekcję logów tylko w staging/development */}
				{this.shouldCaptureConsole && this.state.showLogs && (
					<div
						style={{
							background: "#fff",
							padding: "20px",
							borderRadius: "8px",
							boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
						}}
					>
						<h3 style={{ margin: "0 0 16px 0", color: "#333" }}>
							Logi konsoli ({this.state.consoleLogs.length})
						</h3>
						<div
							style={{
								maxHeight: "400px",
								overflow: "auto",
								background: "#1e1e1e",
								padding: "12px",
								borderRadius: "4px",
							}}
						>
							{this.state.consoleLogs.length === 0 ? (
								<div style={{ color: "#888", fontStyle: "italic" }}>
									Brak logów
								</div>
							) : (
								this.state.consoleLogs.map((log, index) => {
									const time = new Date(log.timestamp).toLocaleTimeString();
									const color =
										log.type === "error"
											? "#f44336"
											: log.type === "warn"
												? "#ff9800"
												: "#4caf50";

									return (
										<div
											key={index}
											style={{
												marginBottom: "8px",
												paddingBottom: "8px",
												borderBottom: "1px solid #333",
												fontSize: "11px",
											}}
										>
											<div style={{ color: "#888", marginBottom: "4px" }}>
												<span
													style={{
														color,
														fontWeight: "600",
														marginRight: "8px",
													}}
												>
													[{log.type.toUpperCase()}]
												</span>
												{time}
											</div>
											<pre
												style={{
													margin: 0,
													color: "#e0e0e0",
													fontFamily: "monospace",
													fontSize: "11px",
													whiteSpace: "pre-wrap",
													wordBreak: "break-word",
												}}
											>
												{log.message}
											</pre>
										</div>
									);
								})
							)}
						</div>
					</div>
				)}
			</div>
		);
	}
}
